import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SendIcon from "@material-ui/icons/Send";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import {
  ModalTitle,
  FormField,
  FormGrid,
  FormSection,
  SwitchRow,
} from "../../components/QueueIntegrationModal/panelKit";

import { DURATION_PRESETS, DURATION_UNITS, copyToClipboard, formatarDuracao } from "./helpers";
import SendBatchDialog from "./SendBatchDialog";

const CUSTOM_DURATION = "custom";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    customDuration: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)",
      gap: 8,
    },
    result: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 14,
      textAlign: "center",
    },
    // o QR precisa de fundo branco para ser lido, também no tema escuro
    qrImg: {
      width: 200,
      height: 200,
      backgroundColor: "#ffffff",
      padding: 8,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
    },
    copyBlock: { width: "100%", textAlign: "left" },
    copyRow: { display: "flex", alignItems: "center", gap: 6 },
    copyField: {
      flex: 1,
      "& .MuiOutlinedInput-input": {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 12,
      },
    },
    successIcon: {
      fontSize: 40,
      color: t.mode === "dark" ? t.status.ok.fg : t.semantic.success,
    },
    successText: { fontSize: 14, color: t.text },
  };
});

// Modal to create a new voucher batch (POST /hotspot/voucher-batches). When a
// Pix charge is generated, shows the QR/copia-e-cola/link result inline.
const NewBatchModal = ({ open, devices, defaultDeviceId, onClose, onCreated }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = !!user && (user.profile === "admin" || !!user.super);

  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("10");
  const [durationOption, setDurationOption] = useState(1440);
  const [customMinutes, setCustomMinutes] = useState("");
  const [customUnit, setCustomUnit] = useState("d");
  const [deviceId, setDeviceId] = useState("");
  const [priceLabel, setPriceLabel] = useState("");
  const [wholesalePrice, setWholesalePrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [generateCharge, setGenerateCharge] = useState(!isAdmin);
  const [chargeTier, setChargeTier] = useState("varejo");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setName("");
      setQuantity("10");
      setDurationOption(1440);
      setCustomMinutes("");
      setCustomUnit("d");
      setDeviceId(defaultDeviceId ? String(defaultDeviceId) : "");
      setPriceLabel("");
      setWholesalePrice("");
      setRetailPrice("");
      setGenerateCharge(!isAdmin);
      setChargeTier("varejo");
      setSaving(false);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const durationMin = useMemo(() => {
    if (durationOption === CUSTOM_DURATION) {
      const unit = DURATION_UNITS.find((u) => u.value === customUnit) || DURATION_UNITS[0];
      return Math.round((Number(customMinutes) || 0) * unit.factor);
    }
    return Number(durationOption) || 0;
  }, [durationOption, customMinutes, customUnit]);

  const quantityNumber = Number(quantity);
  const quantityValid = Number.isInteger(quantityNumber) && quantityNumber >= 1 && quantityNumber <= 500;
  const durationValid = durationMin > 0;
  const nameValid = name.trim().length > 0;
  const chargeValid = !generateCharge || Number(wholesalePrice) > 0 || Number(retailPrice) > 0;
  const formValid = nameValid && quantityValid && durationValid && chargeValid;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!formValid) return;
    setSaving(true);
    try {
      const toCents = (v) => (v === "" || v === null || v === undefined ? undefined : Math.round(Number(v) * 100));
      const body = {
        nome: name.trim(),
        quantidade: quantityNumber,
        duracaoMin: durationMin,
        deviceId: deviceId || undefined,
        precoLabel: priceLabel.trim() || undefined,
        precoAtacadoCent: toCents(wholesalePrice),
        precoVarejoCent: toCents(retailPrice),
        cobranca: generateCharge ? chargeTier : undefined,
      };
      const { data } = await api.post("/hotspot/voucher-batches", body);
      if (!isMountedRef.current) return;
      toast.success(i18n.t("hotspot.toasts.batchCreated"));
      setResult(data);
      if (onCreated) onCreated(data);
      if (!data?.venda) {
        onClose();
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const handleCopy = async (text, toastKey) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(i18n.t(toastKey));
  };

  const venda = result?.venda;

  const quantityError = quantity !== "" && !quantityValid;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <ModalTitle
          title={result ? i18n.t("hotspot.result.title") : i18n.t("hotspot.newBatch.title")}
          onClose={handleClose}
          closeLabel={i18n.t("hotspot.buttons.close")}
        />
        <DialogContent>
          {!result ? (
            <>
              <FormSection>
                <FormGrid>
                  <FormField full required label={i18n.t("hotspot.newBatch.name")} htmlFor="hotspot-batch-name">
                    <TextField
                      id="hotspot-batch-name"
                      placeholder={i18n.t("hotspot.newBatch.namePlaceholder")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      autoFocus
                      disabled={saving}
                    />
                  </FormField>
                  <FormField
                    required
                    label={i18n.t("hotspot.newBatch.quantity")}
                    htmlFor="hotspot-batch-quantity"
                    error={quantityError ? i18n.t("hotspot.newBatch.quantityInvalid") : null}
                  >
                    <TextField
                      id="hotspot-batch-quantity"
                      type="number"
                      inputProps={{ min: 1, max: 500 }}
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={saving}
                      error={quantityError}
                    />
                  </FormField>
                  <FormField required label={i18n.t("hotspot.newBatch.duration")}>
                    <TextField
                      select
                      value={durationOption}
                      onChange={(e) => setDurationOption(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={saving}
                    >
                      {DURATION_PRESETS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {formatarDuracao(opt.value)}
                        </MenuItem>
                      ))}
                      <MenuItem value={CUSTOM_DURATION}>
                        {i18n.t("hotspot.newBatch.durationCustom")}
                      </MenuItem>
                    </TextField>
                  </FormField>
                  {durationOption === CUSTOM_DURATION && (
                    <FormField
                      full
                      label={i18n.t("hotspot.newBatch.durationCustom")}
                      help={durationMin > 0 ? formatarDuracao(durationMin) : null}
                    >
                      <div className={classes.customDuration}>
                        <TextField
                          placeholder={i18n.t("hotspot.newBatch.durationCustomPlaceholder")}
                          type="number"
                          inputProps={{
                            min: 1,
                            "aria-label": i18n.t("hotspot.newBatch.durationCustomPlaceholder"),
                          }}
                          value={customMinutes}
                          onChange={(e) => setCustomMinutes(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={saving}
                        />
                        <TextField
                          select
                          value={customUnit}
                          onChange={(e) => setCustomUnit(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={saving}
                          inputProps={{ "aria-label": i18n.t("hotspot.newBatch.durationUnit") }}
                        >
                          {DURATION_UNITS.map((u) => (
                            <MenuItem key={u.value} value={u.value}>
                              {i18n.t(`hotspot.newBatch.units.${u.value}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </FormField>
                  )}
                  <FormField full label={i18n.t("hotspot.newBatch.device")}>
                    <TextField
                      select
                      value={deviceId}
                      onChange={(e) => setDeviceId(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={saving}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="">{i18n.t("hotspot.newBatch.deviceDefault")}</MenuItem>
                      {(devices || []).map((d) => (
                        <MenuItem key={d.id} value={String(d.id)}>
                          {d.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormField>
                </FormGrid>
              </FormSection>

              <FormSection title="Preço e cobrança">
                <FormGrid>
                  <FormField full label={i18n.t("hotspot.newBatch.priceLabel")} htmlFor="hotspot-batch-price-label">
                    <TextField
                      id="hotspot-batch-price-label"
                      placeholder={i18n.t("hotspot.newBatch.priceLabelPlaceholder")}
                      value={priceLabel}
                      onChange={(e) => setPriceLabel(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={saving}
                    />
                  </FormField>
                  <FormField label={i18n.t("hotspot.newBatch.wholesale")} htmlFor="hotspot-batch-wholesale">
                    <TextField
                      id="hotspot-batch-wholesale"
                      type="number"
                      inputProps={{ min: 0, step: "0.01" }}
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                      value={wholesalePrice}
                      onChange={(e) => setWholesalePrice(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={saving}
                    />
                  </FormField>
                  <FormField label={i18n.t("hotspot.newBatch.retail")} htmlFor="hotspot-batch-retail">
                    <TextField
                      id="hotspot-batch-retail"
                      type="number"
                      inputProps={{ min: 0, step: "0.01" }}
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                      value={retailPrice}
                      onChange={(e) => setRetailPrice(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={saving}
                    />
                  </FormField>
                  <SwitchRow
                    full
                    label={i18n.t("hotspot.newBatch.generateCharge")}
                    help={!isAdmin ? i18n.t("hotspot.newBatch.chargeRequired") : null}
                  >
                    <Switch
                      color="primary"
                      checked={generateCharge}
                      onChange={(e) => setGenerateCharge(e.target.checked)}
                      disabled={saving || !isAdmin}
                      inputProps={{ "aria-label": i18n.t("hotspot.newBatch.generateCharge") }}
                    />
                  </SwitchRow>
                  {generateCharge && (
                    <FormField
                      full
                      label={i18n.t("hotspot.newBatch.generateCharge")}
                      error={!chargeValid ? i18n.t("hotspot.newBatch.chargeRequired") : null}
                    >
                      <TextField
                        select
                        value={chargeTier}
                        onChange={(e) => setChargeTier(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={saving}
                      >
                        <MenuItem value="varejo">{i18n.t("hotspot.newBatch.retail")}</MenuItem>
                        <MenuItem value="atacado">{i18n.t("hotspot.newBatch.wholesale")}</MenuItem>
                      </TextField>
                    </FormField>
                  )}
                </FormGrid>
              </FormSection>
            </>
          ) : (
            <div className={classes.result}>
              {venda ? (
                <>
                  {venda.qrcodeBase64 && (
                    <img
                      className={classes.qrImg}
                      src={`data:image/png;base64,${venda.qrcodeBase64}`}
                      alt={i18n.t("hotspot.result.qrTitle")}
                    />
                  )}
                  {venda.pixCopiaCola && (
                    <FormField className={classes.copyBlock} label={i18n.t("hotspot.result.pixCopyPaste")}>
                      <div className={classes.copyRow}>
                        <TextField
                          className={classes.copyField}
                          value={venda.pixCopiaCola}
                          variant="outlined"
                          size="small"
                          InputProps={{ readOnly: true }}
                          inputProps={{ "aria-label": i18n.t("hotspot.result.pixCopyPaste") }}
                        />
                        <Tooltip title={i18n.t("hotspot.buttons.copy")}>
                          <IconButton
                            aria-label={`${i18n.t("hotspot.buttons.copy")} — ${i18n.t("hotspot.result.pixCopyPaste")}`}
                            onClick={() => handleCopy(venda.pixCopiaCola, "hotspot.toasts.pixCopied")}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </FormField>
                  )}
                  {venda.link && (
                    <FormField className={classes.copyBlock} label={i18n.t("hotspot.result.link")}>
                      <div className={classes.copyRow}>
                        <TextField
                          className={classes.copyField}
                          value={venda.link}
                          variant="outlined"
                          size="small"
                          InputProps={{ readOnly: true }}
                          inputProps={{ "aria-label": i18n.t("hotspot.result.link") }}
                        />
                        <Tooltip title={i18n.t("hotspot.buttons.copy")}>
                          <IconButton
                            aria-label={`${i18n.t("hotspot.buttons.copy")} — ${i18n.t("hotspot.result.link")}`}
                            onClick={() => handleCopy(venda.link, "hotspot.toasts.linkCopied")}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </FormField>
                  )}
                </>
              ) : (
                <>
                  <CheckCircleIcon className={classes.successIcon} />
                  <div className={classes.successText}>{i18n.t("hotspot.toasts.batchCreated")}</div>
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {!result ? (
            <>
              <Button onClick={handleClose} disabled={saving}>
                {i18n.t("hotspot.buttons.cancel")}
              </Button>
              <ButtonWithSpinner
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                loading={saving}
                disabled={saving || !formValid}
              >
                {saving ? i18n.t("hotspot.newBatch.creating") : i18n.t("hotspot.newBatch.submit")}
              </ButtonWithSpinner>
            </>
          ) : (
            <>
              {venda && (
                <Button variant="outlined" startIcon={<SendIcon />} onClick={() => setSendOpen(true)}>
                  {i18n.t("hotspot.buttons.sendWhatsapp")}
                </Button>
              )}
              <Button onClick={onClose} color="primary" variant="contained">
                {i18n.t("hotspot.result.done")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <SendBatchDialog open={sendOpen} batch={result} onClose={() => setSendOpen(false)} />
    </>
  );
};

export default NewBatchModal;
